import { useCallback, useEffect, useRef, useState } from 'react';
import { BarChart3, BookOpen, HelpCircle, LogOut, MoreHorizontal, Share2, X } from 'lucide-react';
import { useDialogTrap } from '../letters/useDialogTrap';
import { useMeQuery } from '../profiles/api';
import { HowItWorksCards } from './HowItWorks';
import { ALL_HOW_CARDS } from './steps';
import { track } from './track';
import { ShareKitSheet } from './ShareKit';
import { STAMP_BOOK_HASH } from '../postage/StampBook';

/**
 * "What is toiyp?" (onboarding plan, slice 2): the three cards from the
 * first-run tour again, and a short FAQ in the postie's voice. Opened from
 * the ⋯ menu beside your avatar in the header.
 */

export const FAQ: { q: string; a: string }[] = [
  {
    q: 'Why so slow?',
    a: "Because the best letters aren't written in a hurry. Every letter is collected and delivered like real post, between 8am and 5pm, Monday to Saturday, so it usually arrives later the same day or the next. The wait gives you both room to write properly.",
  },
  {
    q: 'Why no read receipts?',
    a: "Nobody should feel watched while they find the right words. You won't see the moment your letter is opened. When they write back, their reply tells you how many times your letter was read, and that's all.",
  },
  {
    q: 'Who can see my address?',
    a: 'Your toiyp, yourname@toiyp.me, is yours to hand out to anyone. Your phone number and home address are different: nobody sees them unless you choose to share them, one person at a time.',
  },
  {
    q: 'How do I share my number later?',
    a: "Keep writing. Once the two of you are in trust, a Contact details card opens in your conversation, and you pick what to share: your number, your address, or neither. It's always your call.",
  },
  {
    q: 'What happens if someone emails my toiyp?',
    a: "It arrives here as a letter, like any other post. When you reply, they get an email inviting them to join toiyp and read it. Your own email address is never shown to them.",
  },
];

export function WhatIsToiypDialog({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const { data: me } = useMeQuery();
  useDialogTrap(ref, onClose);

  return (
    <div className="how-dialog__backdrop" onClick={onClose}>
      <div
        className="how-dialog how-dialog--about"
        role="dialog"
        aria-modal="true"
        aria-label="What is toiyp?"
        tabIndex={-1}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="how-dialog__close icon-btn" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <p className="first-run__kicker">What is toiyp?</p>
        <HowItWorksCards cards={ALL_HOW_CARDS} index={index} onIndexChange={setIndex} handle={me?.handle ?? undefined} />

        <section className="about-faq" aria-label="Questions people ask">
          <h2 className="about-faq__title">Questions people ask</h2>
          {FAQ.map(({ q, a }) => (
            <details key={q} className="about-faq__item">
              <summary className="about-faq__q">{q}</summary>
              <p className="about-faq__a">{a}</p>
            </details>
          ))}
        </section>

        <button type="button" className="btn btn--primary btn--block" onClick={onClose}>
          Back to my letters
        </button>
      </div>
    </div>
  );
}

/**
 * The header's ⋯ menu, beside your avatar. On narrow screens it also holds
 * the admin dashboard and sign-out, so every header control fits on a phone.
 */
export function HeaderMenu({
  onAdmin,
  onSignOut,
}: {
  /** Shown as an item when the header has no room for its own button. */
  onAdmin?: () => void;
  onSignOut?: () => void;
} = {}) {
  const [open, setOpen] = useState(false);
  const [about, setAbout] = useState(false);
  const [sharing, setSharing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const openAbout = useCallback(() => {
    setOpen(false);
    setAbout(true);
    track({ event: 'about_opened' });
  }, []);

  return (
    <div className="conv-menu header-menu" ref={ref}>
      <button
        type="button"
        className="icon-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More"
        title="More"
      >
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div className="conv-menu__pop header-menu__pop" role="menu" aria-label="More">
          <button
            type="button"
            role="menuitem"
            className="conv-menu__item"
            onClick={() => {
              setOpen(false);
              setSharing(true);
            }}
          >
            <Share2 size={14} aria-hidden="true" />
            <span>
              Share your toiyp
              <span className="conv-menu__hint">Your address, a story card, a code to scan</span>
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="conv-menu__item"
            onClick={() => {
              setOpen(false);
              window.location.hash = STAMP_BOOK_HASH;
            }}
          >
            <BookOpen size={14} aria-hidden="true" />
            <span>
              Stamp book
              <span className="conv-menu__hint">Your stamps, and the special editions</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="conv-menu__item" onClick={openAbout}>
            <HelpCircle size={14} aria-hidden="true" />
            <span>
              What is toiyp?
              <span className="conv-menu__hint">How it works, and the questions people ask</span>
            </span>
          </button>
          {onAdmin && (
            <button
              type="button"
              role="menuitem"
              className="conv-menu__item"
              onClick={() => {
                setOpen(false);
                onAdmin();
              }}
            >
              <BarChart3 size={14} aria-hidden="true" />
              <span>Admin dashboard</span>
            </button>
          )}
          {onSignOut && (
            <button
              type="button"
              role="menuitem"
              className="conv-menu__item"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              <LogOut size={14} aria-hidden="true" />
              <span>Sign out</span>
            </button>
          )}
        </div>
      )}
      {about && <WhatIsToiypDialog onClose={() => setAbout(false)} />}
      {sharing && <ShareKitSheet from="menu" onClose={() => setSharing(false)} />}
    </div>
  );
}
